import {Injectable} from '@angular/core';
import {TrackingService} from '../../../services/tracking.service';
import {SelectedProductPlan} from '../../../types/site.type';
import {ProductNamesLabel} from '../../../constants/products';
import {getAddonLabelFromType, getPlanPeriodFromBillingCycle} from '../../../common/utils/gtm';
import {UserInfoEventData} from '../../../common/models/interfaces';
import {SubscriptionBillingCycleType} from '../../../../userway/sdk/UserWayApiClient';
import {BillingCycleType} from '../../../types/payment.type';

declare global {
  interface Window {
    dataLayer: any;
  }
}

export interface ILeadEvent {
  fullName: string;
  companyName: string;
  email: string;
  websiteUrl: string;
  timestamp?: number;
}

export interface IAcceptTermsEvent {
  userway_user_id: string;
  userway_user_name: string;
  userway_user_email: string;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityTrackingService {
  constructor(private trackingService: TrackingService) {}
  pushToDataLayer(data?: any) {
    const dataLayer = window.dataLayer || [];
    dataLayer.push(data);
    console.info('[gtm] event pushed => ', data);
  }

  trackLeadEvent({fullName, companyName, email, websiteUrl}: ILeadEvent) {
    try {
      const timestamp = Date.now();
      this.pushTag({
        event: 'lead',
        lead_type: 'manage-console',
        gtm_trigger: 'true',
        fullName,
        companyName,
        email,
        websiteUrl,
        timestamp,
      });
      this.trackingService.trackEvent({
        event: 'lead-manage-console',
        fullName,
        companyName,
        email,
        websiteUrl,
        timestamp,
      });
    } catch (e) {
      console.error('[gtm] error while pushing event => ', e);
    }
  }

  // Drawer Stats tab
  enableLiveTranslationEvent() {
    this.pushTag({
      event: 'enable-translation',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'enable-translation',
    });
  }

  // Drawer Addons tab
  selectTranslations1Event() {
    this.pushTag({
      event: 'select-translation-1',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'select-translation-1',
    });
  }

  selectTranslations5Event() {
    this.pushTag({
      event: 'select-translation-5',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'select-translation-5',
    });
  }

  selectTranslations10Event() {
    this.pushTag({
      event: 'select-translation-10',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'select-translation-10',
    });
  }

  // Side nav
  sideNavAddSiteEvent() {
    this.pushTag({
      event: 'nav-add-sites',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'nav-add-sites',
    });
  }

  // Hero
  heroAddSitesEvent() {
    this.pushTag({
      event: 'hero-add-sites',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'hero-add-sites',
    });
  }

  heroImportCsvEvent() {
    this.pushTag({
      event: 'hero-import-csv',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'hero-import-csv',
    });
  }

  heroUpdatePlansEvent() {
    this.pushTag({
      event: 'hero-update-plans',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'hero-update-plans',
    });
  }

  // Site details
  siteDetailsRowEvent(siteHost: string) {
    this.pushTag({
      event: 'site-details-row',
      site: siteHost,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'site-details-row',
      site: siteHost,
    });
  }

  siteDetailsUpgradeEvent() {
    this.pushTag({
      event: 'site-details-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'site-details-upgrade',
    });
  }

  startTrialEvent() {
    this.pushTag({
      event: 'trial-sign-up',
      gtm_trigger: 'true',
    });
  }

  // Drawer Settings tab
  toggleWidgeSettingstEvent(state: boolean, type: 'general' | 'mobile' | 'sound') {
    this.pushTag({
      event: `widget-${type === 'general' ? '' : `${type}-`}${state ? 'enable' : 'disable'}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `widget-${type === 'general' ? '' : `${type}-`}${state ? 'enable' : 'disable'}`,
    });
  }

  accessibilityStatementLinkApplyEvent(type: 'all' | 'one') {
    this.pushTag({
      event: `acc-statement-link-${type}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `acc-statement-link-${type}`,
    });
  }

  statementInputFocusEvent(type: 'title' | 'content') {
    this.pushTag({
      event: `acc-statement-${type}-focus`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `acc-statement-${type}-focus`,
    });
  }

  statementDisclaimerClick(userInfoEventData: UserInfoEventData) {
    this.pushTag({
      event: `acc-statement-disclaimer`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `acc-statement-disclaimer`,
      ...userInfoEventData,
    });
  }

  statementDisclaimerCheckbox(value: boolean, userInfoEventData: UserInfoEventData) {
    this.pushTag({
      event: `acc-statement-disclaimer-checkbox-${value}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `acc-statement-disclaimer-checkbox-${value}`,
      ...userInfoEventData,
    });
  }

  statementDisclaimerAcceptButton(userInfoEventData: UserInfoEventData) {
    this.pushTag({
      event: `acc-statement-disclaimer-accept-button`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `acc-statement-disclaimer-accept-button`,
      ...userInfoEventData,
    });
  }

  deleteSiteEvent() {
    this.pushTag({
      event: 'delete-site',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'delete-site',
    });
  }

  // Widget Embed Script component
  widgetEmbedScriptChangeTabEvent(tab: string) {
    this.pushTag({
      event: `script-${tab}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `script-${tab}`,
    });
  }

  // Add sites
  addSitesShowComparisonEvent() {
    this.pushTag({
      event: 'show-comparison',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'show-comparison',
    });
  }

  addSitesChooseWidgetEvent(widgetType: SelectedProductPlan) {
    this.pushTag({
      event: `${widgetType}-widget`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `${widgetType}-widget`,
    });
  }

  addSiteInputFocusEvent() {
    this.pushTag({
      event: 'add-site-focus',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'add-site-focus',
    });
  }

  addSiteContinueButtonEvent() {
    this.pushTag({
      event: 'add-site-continue',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'add-site-continue',
    });
  }

  // Payment form
  seePlanPricesEvent(plan: SubscriptionBillingCycleType) {
    const currentPeriod = getPlanPeriodFromBillingCycle(plan, 'a11-plan');

    this.pushTag({
      event: `see-${currentPeriod}-prices`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `see-${currentPeriod}-prices`,
    });
  }

  plansWebsiteInputFocusEvent() {
    this.pushTag({
      event: 'plans-site-focus',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'plans-site-focus',
    });
  }

  plansAddWebsiteClickEvent() {
    this.pushTag({
      event: 'plans-add-site',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'plans-add-site',
    });
  }

  plansContinuePaymentClickEvent() {
    this.pushTag({
      event: 'plans-continue-payment',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'plans-continue-payment',
    });
  }

  addonContinuePaymentClickEvent(addonType: ProductNamesLabel, addonPeriod: BillingCycleType) {
    const currentAddonPeriod = getPlanPeriodFromBillingCycle(addonPeriod, 'addon');
    const currentAddonType = getAddonLabelFromType(addonType);

    this.pushTag({
      event: `${currentAddonType}-continue-payment-${currentAddonPeriod}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `${currentAddonType}-continue-payment-${currentAddonPeriod}`,
    });
  }

  completePaymentClickEvent(period: BillingCycleType, status: 'success' | 'error') {
    const currentPeriod = getPlanPeriodFromBillingCycle(period, 'addon');
    this.pushTag({
      event: `complete-payment${status === 'error' ? '-error' : ''}-${currentPeriod}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `complete-payment${status === 'error' ? '-error' : ''}-${currentPeriod}`,
    });
  }

  // Drawer Overview tab
  ctaTopUpgradeEvent() {
    this.pushTag({
      event: 'cta-top-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'cta-top-upgrade',
    });
  }

  commitmentUpgradeEvent() {
    this.pushTag({
      event: 'commitment-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'commitment-upgrade',
    });
  }

  scoreExplainEvent() {
    this.pushTag({
      event: 'acc-score-explain',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'acc-score-explain',
    });
  }

  seeReportEvent() {
    this.pushTag({
      event: 'see-report',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'see-report',
    });
  }

  // Drawer - appearance tab
  applyTypeAllSitesEvent() {
    this.pushTag({
      event: 'type-apply-all',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'type-apply-all',
    });
  }

  applyTypeEvent() {
    this.pushTag({
      event: 'type-apply-one',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'type-apply-one',
    });
  }

  typeUpgradeEvent() {
    this.pushTag({
      event: 'appearance-type-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'appearance-type-upgrade',
    });
  }

  widgetCustomTrialEvent() {
    this.pushTag({
      event: 'widget-custom-start-trial',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'widget-custom-start-trial',
    });
  }

  widgetCustomUpgradeEvent() {
    this.pushTag({
      event: 'widget-custom-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'widget-custom-upgrade',
    });
  }

  modifyMenuTrialEvent() {
    this.pushTag({
      event: 'modify-menu-start-trial',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'modify-menu-start-trial',
    });
  }

  modifyMenuUpgradeEvent() {
    this.pushTag({
      event: 'modify-menu-upgrade',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'modify-menu-upgrade',
    });
  }

  // Drawer Report tab
  getReportEvent() {
    this.pushTag({
      event: 'issues-get-report',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'issues-get-report',
    });
  }

  // Walkthrough
  startWalkthroughEvent() {
    this.pushTag({
      event: 'walkthrough-start',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'walkthrough-start',
    });
  }

  stopWalkthroughEvent(step: number) {
    this.pushTag({
      event: `walkthrough-${step}-stop`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `walkthrough-${step}-stop`,
    });
  }

  nextWalkthroughStepEvent(step: number) {
    this.pushTag({
      event: `walkthrough-${step}-next-step`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `walkthrough-${step}-next-step`,
    });
  }

  completeWalkthroughEvent() {
    this.pushTag({
      event: 'walkthrough-complete',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'walkthrough-complete',
    });
  }

  becomePartnerEvent() {
    this.pushTag({
      event: 'sidebar-cta-become-partner',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'sidebar-cta-become-partner',
    });
  }

  registerRedirectEvent() {
    this.pushTag({
      event: 'cta-register-redirect',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'cta-register-redirect',
    });
  }

  liveTranslationBtnChangeStateEvent(state: string) {
    this.pushTag({
      event: `lst_btn_${state}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `lst_btn_${state}`,
    });
  }
  googleAnalyticsStateChangedEvent(state: string) {
    this.pushTag({
      event: `g_analytics_${state}`,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: `g_analytics_${state}`,
    });
  }

  //Accessibility Statement
  downloadStatementEvent(fromGeneratedView: boolean = false) {
    const event = `acc-statement-download-${fromGeneratedView ? 'generated' : 'last'}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }
  customizeStatementEvent(overviewMode: boolean = false) {
    const event = `acc-statement-customize${overviewMode ? '-from-overview' : ''}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }

  selectA11yStatementProductEvent(product: string) {
    const event = `acc-statement-select-product-${product}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }

  showAccessibilityStatementEvent(value: boolean) {
    const event = `acc-statement-${value ? 'show' : 'hide'}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }

  generateAccessibilityStatementEvent() {
    this.pushTag({
      event: 'acc-statement-generate',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'acc-statement-generate',
    });
  }

  showReportAProblemEvent(value: boolean) {
    const event = `report-a-problem-${value ? 'show' : 'hide'}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }

  accessibilityStatementApplyEvent() {
    this.pushTag({
      event: 'acc-statement-one',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'acc-statement-one',
    });
  }

  enableAccessibilityStatementEvent(value: boolean) {
    const event = `acc-statement-${value ? 'enable' : 'disable'}`;
    this.pushTag({
      event,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event,
    });
  }

  createGroupEvent() {
    this.pushTag({
      event: 'create-group',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'create-group',
    });
  }

  inviteMembersToGroupEvent() {
    this.pushTag({
      event: 'invite-members-to-group',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'invite-members-to-group',
    });
  }

  updateGroupMembersEvent() {
    this.pushTag({
      event: 'update-group-members',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'update-group-members',
    });
  }

  manageGroupEvent() {
    this.pushTag({
      event: 'manage-group',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'manage-group',
    });
  }

  updateTeamGroupSitesEvent() {
    this.pushTag({
      event: 'update-group-sites',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'update-group-sites',
    });
  }

  updateTeamUserSitesEvent() {
    this.pushTag({
      event: 'update-user-sites',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'update-user-sites',
    });
  }

  deleteGroupEvent() {
    this.pushTag({
      event: 'delete-group',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'delete-group',
    });
  }

  allUsersTabEvent() {
    this.pushTag({
      event: 'team-users',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'team-users',
    });
  }

  groupsTabEvent() {
    this.pushTag({
      event: 'team-groups',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'team-groups',
    });
  }

  addUserEvent() {
    this.pushTag({
      event: 'add-user',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'add-user',
    });
  }

  openUserDetailsEvent() {
    this.pushTag({
      event: 'team-user-details',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'team-user-details',
    });
  }

  updateGroupMemberInfoEvent() {
    this.pushTag({
      event: 'update-group-member-info',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'update-group-member-info',
    });
  }

  manageAccountsEvent() {
    this.pushTag({
      event: 'manage-accounts',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'manage-accounts',
    });
  }

  manageMainAccountEvent() {
    this.pushTag({
      event: 'manage-main-account',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'manage-main-account',
    });
  }

  addSubAccountEvent() {
    this.pushTag({
      event: 'add-sub-account',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'add-sub-account',
    });
  }

  manageSubAccountEvent() {
    this.pushTag({
      event: 'manage-sub-account',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'manage-sub-account',
    });
  }

  updateGroupSettings() {
    this.pushTag({
      event: 'update-group-settings',
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: 'update-group-settings',
    });
  }

  acceptTermsEvent(data: IAcceptTermsEvent & ILeadEvent) {
    this.pushTag({
      event: 'accept-terms',
      gtm_trigger: 'true',
      websiteUrl: data.websiteUrl,
      companyName: data.companyName,
      timestamp: data.timestamp,
    });
    this.trackingService.trackEvent({
      ...data,
      event: 'accept-terms',
    });
  }

  pushTag(tagData: {event: string; gtm_trigger: string; lead_type?: string; site?: string} & Partial<ILeadEvent>) {
    return this.trackingService.pushGtmTag(tagData).catch(() => {});
  }
}
